import { Languages } from "@tree-company/collect-js-client";
import FR_TRANSLATIONS from "./fr.json";
import NL_TRANSLATIONS from "./nl.json";
import DE_TRANSLATIONS from "./de.json";

interface Recursive<T> {
  [keys: string]: T;
}

type Translations = Recursive<Translations>;

const TRANSLATIONS: { nl: Translations; fr: Translations; de: Translations } = {
  nl: NL_TRANSLATIONS,
  fr: FR_TRANSLATIONS,
  de: DE_TRANSLATIONS,
};

export const DEFAULT_LANGUAGE = Languages.nl;

/**
 * Resolve the translation
 *
 * @param {string | Array<string>} key_or_keys - The key to translate, or an array of keys
 * @param {Record<string, string>} interpolations - Values to be filled in the translation
 * @param {string} language - The language to translate to
 * @returns string
 */
export default function translate(
  key_or_keys: string | string[],
  interpolations: Record<string, string> = {},
  language: Languages = DEFAULT_LANGUAGE,
): string | undefined {
  const keys = normalizeKey(key_or_keys);
  let translation = keys.reduce<Translations | undefined>(
    (scope, key) => {
      if (scope) return scope[key];
    },
    TRANSLATIONS[language as keyof typeof TRANSLATIONS],
  ) as string | undefined;

  if (translation === undefined) {
    // This should only happen during development
    // eslint-disable-next-line no-console
    console.error(`Translation ${keys.join(".")} could not be found`);
  } else if (typeof translation === "string") {
    Object.entries(interpolations).forEach(
      ([key, value]) =>
        (translation = translation!.replaceAll(`{{${key}}}`, value)),
    );
  }

  return translation;
}

function normalizeKey(key_or_keys: string | string[]) {
  const array = Array.isArray(key_or_keys) ? key_or_keys : [key_or_keys];
  return array.filter((i) => i !== null).flatMap((key) => key?.split("."));
}
